import * as React from "react";
import { getWindowCenterPosition, getMobileSize } from "../desktopWindowUtils";
import { WindowTile } from "../desktopWindowTypes";
import { ResizeDirection } from "re-resizable";
import { Position, ResizableDelta } from "react-rnd";
import { isMobile } from "react-device-detect";

export type AtLeast<T, K extends keyof T> = Partial<T> & Pick<T, K>;

const defaultTileValue: Omit<WindowTile, "id" | "componentName"> = {
	windowTitle: "Untitled",
	size: {
		width: 400,
		height: 400,
	},
	position: {
		x: 100,
		y: 100,
	},
};

export const useWindowTiles = () => {
	const [windowTiles, setWindowTiles] = React.useState<WindowTile[]>([]);
	const [focusedTileId, setFocusedTileId] = React.useState<string>();

	const openWindow = React.useCallback((values: AtLeast<WindowTile, "id" | "componentName">) => {
		setWindowTiles((prevTiles) => {
			const isWindowOpened = prevTiles.some((prevTile) => prevTile.id === values.id);
			if (!isWindowOpened) {
				//@ts-ignore
				const size: { width: number; height: number } = isMobile ? getMobileSize(true) : values.size;
				const position = values.position
					? values.position
					: isMobile
					? { x: 0, y: 0 }
					: getWindowCenterPosition(values.size || defaultTileValue.size);
				// Create new window
				const tile: WindowTile = {
					...defaultTileValue,
					...values,
					size,
					resizeable: isMobile ? false : values.resizeable,
					draggable: isMobile,
					open: true,
					isMinimize: false,
					position,
				};

				// Add to array
				return [...prevTiles, tile];
			}

			// Set window tile to open
			return prevTiles.map((prevTile) => {
				if (prevTile.id === values.id) {
					return {
						...prevTile,
						open: true,
						isMinimize: false,
					};
				}
				return prevTile;
			});
		});

		setFocusedTileId(values.id);
	}, []);

	const closeWindow = React.useCallback((id: string) => {
		setWindowTiles((prev) => prev.filter((tile) => tile.id !== id));

		setFocusedTileId((prev) => (prev === id ? undefined : prev));
	}, []);

	const minimizeWindow = React.useCallback((id: string) => {
		setWindowTiles((prev) =>
			prev.map((tile) => {
				if (tile.id === id) {
					return {
						...tile,
						open: false,
						isMinimize: true,
					};
				}
				return tile;
			}),
		);

		setFocusedTileId((prev) => (prev === id ? undefined : prev));
	}, []);

	const isWindowOpened = React.useCallback(
		(id: string) => {
			return windowTiles.some((tile) => tile.id === id);
		},
		[windowTiles],
	);

	const isMinimized = React.useCallback(
		(id: string) => {
			return windowTiles.some((tile) => tile.id === id && tile.isMinimize === true);
		},
		[windowTiles],
	);

	const windowResized = React.useCallback(
		(
			id: string,
			e: MouseEvent | TouchEvent,
			dir: ResizeDirection,
			ref: HTMLElement,
			delta: ResizableDelta,
			position: Position,
		) => {
			setWindowTiles((prev) =>
				prev.map((tile) => {
					if (tile.id === id) {
						return {
							...tile,
							size: {
								width: ref.clientWidth,
								height: ref.clientHeight,
							},
							position,
						};
					}
					return tile;
				}),
			);
		},
		[],
	);
	// id can be an id such as chat, notes
	// or the component name of file tile as they have each a uniq id based on the name of the file making harder to track;
	const MobileWindowResized = React.useCallback((id: string, fullHeight: boolean) => {
		setWindowTiles((prev) =>
			prev.map((tile) => {
				if (tile.id === id || tile.componentName === id) {
					const size = getMobileSize(fullHeight);
					return {
						...tile,
						size: {
							width: size.width,
							height: size.height,
						},
					};
				}
				return tile;
			}),
		);
	}, []);

	const setWindowToFocus = React.useCallback((tileId: string) => {
		setFocusedTileId(tileId);

		setWindowTiles((prev) =>
			prev.map((tile) => {
				if (tile.id === tileId) {
					return {
						...tile,
						open: true,
						isMinimize: false,
					};
				}
				return tile;
			}),
		);
	}, []);

	const setWindowToBlur = React.useCallback((tileId: string) => {
		setFocusedTileId((prevTileId) => (prevTileId === tileId ? undefined : prevTileId));
	}, []);

	return React.useMemo(
		() => ({
			openWindow,
			closeWindow,
			windowTiles,
			minimizeWindow,
			isWindowOpened,
			isMinimized,
			windowResized,
			focusedTileId,
			setWindowToFocus,
			setWindowToBlur,
			MobileWindowResized,
		}),
		[
			openWindow,
			closeWindow,
			windowTiles,
			minimizeWindow,
			isWindowOpened,
			isMinimized,
			windowResized,
			focusedTileId,
			setWindowToFocus,
			setWindowToBlur,
			MobileWindowResized,
		],
	);
};
